import * as THREE from 'three';
import type { ColorId, GameState } from '@color-dots/game';
import { posEqual } from '@color-dots/game';
import type { VisualStyle } from '../game/visualStyles.js';
import { buildSphereArc, curvePointMatrix, projectOntoSphere } from './arcCurve3d.js';
import { getColorHex } from './colors3d.js';
import {
  gearMetalMaterialEmissive,
  gearPathEmissiveIntensity,
} from './gearEmissivePolicy.js';
import {
  CHAIN_LINKS_PER_CELL,
  createChainLinkGeometry,
} from './gearGeometry3d.js';
import { gridToWorld } from './Grid3DLayout.js';

const ARC_TUBE_SEGMENTS = 12;
const ARC_RADIAL_SEGMENTS = 10;

export class PathsMeshes {
  readonly group = new THREE.Group();
  private segmentMeshes: THREE.Object3D[] = [];
  private visualStyle: VisualStyle = 'pipe';
  private sphereCenter = new THREE.Vector3(0, 0, 0);
  private sphereRadius = 0;

  setVisualStyle(style: VisualStyle): void {
    this.visualStyle = style;
  }

  getVisualStyle(): VisualStyle {
    return this.visualStyle;
  }

  /** Sphere center/radius (board-local space) used to bulge path arcs toward the glass sphere surface. */
  setSphereBounds(center: THREE.Vector3, radius: number): void {
    this.sphereCenter.copy(center);
    this.sphereRadius = radius;
  }

  sync(state: GameState, activeColor: ColorId | null): void {
    this.clear();

    if (this.visualStyle === 'gear') {
      this.syncGearChains(state, activeColor);
      return;
    }

    this.syncPipePaths(state, activeColor);
  }

  private syncPipePaths(state: GameState, activeColor: ColorId | null): void {
    for (const [color, path] of state.paths) {
      if (path.length === 0) continue;
      const isActive = color === activeColor;
      const endpoints = state.endpoints.get(color);
      const base = getColorHex(color);
      const radius = isActive ? 0.27 : 0.2;
      const lift = radius * (isActive ? 0.6 : 0.4);

      for (let i = 0; i < path.length - 1; i++) {
        const [x1, y1] = path[i]!;
        const [x2, y2] = path[i + 1]!;
        const start = this.projectNode(x1, y1, state.size, lift);
        const end = this.projectNode(x2, y2, state.size, lift);
        const curve = buildSphereArc(start, end, this.sphereCenter, this.sphereRadius);
        const geo = new THREE.TubeGeometry(curve, ARC_TUBE_SEGMENTS, radius, ARC_RADIAL_SEGMENTS, false);
        const mat = new THREE.MeshStandardMaterial({
          color: base,
          emissive: base,
          emissiveIntensity: isActive ? 0.52 : 0.2,
          roughness: isActive ? 0.28 : 0.45,
          metalness: 0.1,
        });
        const seg = new THREE.Mesh(geo, mat);
        seg.castShadow = true;
        this.group.add(seg);
        this.segmentMeshes.push(seg);
      }

      for (const pos of path) {
        if (endpoints && (posEqual(pos, endpoints[0]) || posEqual(pos, endpoints[1]))) {
          continue;
        }
        const geo = new THREE.SphereGeometry(radius, 12, 12);
        const mat = new THREE.MeshStandardMaterial({
          color: base,
          emissive: base,
          emissiveIntensity: isActive ? 0.48 : 0.18,
          roughness: isActive ? 0.26 : 0.42,
        });
        const node = new THREE.Mesh(geo, mat);
        node.position.copy(this.projectNode(pos[0], pos[1], state.size, lift));
        this.group.add(node);
        this.segmentMeshes.push(node);
      }
    }
  }

  private syncGearChains(state: GameState, activeColor: ColorId | null): void {
    const linkGeo = createChainLinkGeometry();
    const matrix = new THREE.Matrix4();

    for (const [color, path] of state.paths) {
      if (path.length < 2) continue;
      const isActive = color === activeColor;
      const base = getColorHex(color);
      const lift = isActive ? 0.08 : 0.05;
      const linkCount = (path.length - 1) * CHAIN_LINKS_PER_CELL;
      const metalEmissive = gearMetalMaterialEmissive();
      const instanced = new THREE.InstancedMesh(
        linkGeo,
        new THREE.MeshStandardMaterial({
          color: base,
          emissive: isActive ? base : metalEmissive.emissive,
          emissiveIntensity: gearPathEmissiveIntensity(isActive),
          roughness: 0.35,
          metalness: 0.65,
        }),
        linkCount,
      );
      instanced.castShadow = true;

      let instanceIdx = 0;
      for (let i = 0; i < path.length - 1; i++) {
        const [x1, y1] = path[i]!;
        const [x2, y2] = path[i + 1]!;
        const start = this.projectNode(x1, y1, state.size, lift);
        const end = this.projectNode(x2, y2, state.size, lift);
        const curve = buildSphereArc(start, end, this.sphereCenter, this.sphereRadius);

        for (let l = 0; l < CHAIN_LINKS_PER_CELL; l++) {
          const t = (l + 0.5) / CHAIN_LINKS_PER_CELL;
          curvePointMatrix(curve, t, matrix);
          instanced.setMatrixAt(instanceIdx, matrix);
          instanceIdx++;
        }
      }
      instanced.instanceMatrix.needsUpdate = true;
      this.group.add(instanced);
      this.segmentMeshes.push(instanced);
    }
  }

  /** Grid cell position, lifted radially off the glass sphere's surface (or raw when no sphere is set). */
  private projectNode(col: number, row: number, size: number, lift: number): THREE.Vector3 {
    const world = gridToWorld(col, row, size);
    if (this.sphereRadius < 1e-6) return world;
    return projectOntoSphere(world, this.sphereCenter, this.sphereRadius, lift);
  }

  private clear(): void {
    for (const mesh of this.segmentMeshes) {
      if (mesh instanceof THREE.InstancedMesh) {
        mesh.geometry.dispose();
        (mesh.material as THREE.Material).dispose();
      } else if (mesh instanceof THREE.Mesh) {
        mesh.geometry.dispose();
        (mesh.material as THREE.Material).dispose();
      }
      this.group.remove(mesh);
    }
    this.segmentMeshes = [];
  }

  dispose(): void {
    this.clear();
  }
}
